(() => {
  if (customElements.get('compre-junto')) {
    return;
  }

  class CompreJunto extends HTMLElement {
    constructor() {
      super();

      this.configNode = this.querySelector('[data-compre-junto-config]');
      if (!this.configNode) return;

      this.config = JSON.parse(this.configNode.textContent);
      this.mainImage = this.querySelector('[data-main-image]');
      this.mainPrice = this.querySelector('[data-main-price]');
      this.mainOptionSelects = Array.from(
        this.querySelectorAll('[data-main-option-select]')
      );
      this.relatedOptionSelects = Array.from(
        this.querySelectorAll('[data-related-option-select]')
      );
      this.relatedImage = this.querySelector('[data-related-image]');
      this.relatedPrice = this.querySelector('[data-related-price]');
      this.relatedCompareAt = this.querySelector('[data-related-compare-at]');
      this.relatedStatus = this.querySelector('[data-related-status]');
      this.totalPrice = this.querySelector('[data-total-price]');
      this.submitButton = this.querySelector('[data-compre-junto-submit]');
      this.errorWrapper = this.querySelector('[data-error-wrapper]');
      this.cartDrawer = document.querySelector('cart-drawer');
      this.mainProductRoot = document.querySelector('[data-main-product]');
      this.mainProductSectionId =
        this.config.mainProductSectionId ||
        this.mainProductRoot?.dataset.section ||
        this.config.sectionId;
      this.mainVariantInput = this.getMainVariantInput();
      this.mainProductSelector = this.getMainProductSelector();
      this.mainVariants = this.config.mainProduct.variants || [];
      this.relatedVariants = this.config.relatedProduct.variants || [];
      this.isLoading = false;
    }

    connectedCallback() {
      if (!this.configNode || !this.submitButton) return;

      this.onRelatedVariantChange = this.onRelatedVariantChange.bind(this);
      this.onMainVariantChange = this.onMainVariantChange.bind(this);
      this.onMainOptionChange = this.onMainOptionChange.bind(this);
      this.onSubmit = this.onSubmit.bind(this);

      this.mainOptionSelects.forEach(select =>
        select.addEventListener('change', this.onMainOptionChange)
      );
      this.relatedOptionSelects.forEach(select =>
        select.addEventListener('change', this.onRelatedVariantChange)
      );
      this.mainVariantInput?.addEventListener('change', this.onMainVariantChange);
      this.submitButton.addEventListener('click', this.onSubmit);

      this.render();
    }

    getMainVariantInput() {
      if (this.config?.productFormId) {
        const input =
          document.querySelector(`#${this.config.productFormId} [name="id"]`) ||
          document.querySelector(`[form="${this.config.productFormId}"][name="id"]`);

        if (input) return input;
      }

      return this.mainProductRoot?.querySelector('[name="id"][form], form [name="id"]') || null;
    }

    getMainProductSelector() {
      if (this.mainProductSectionId) {
        const selector = document.querySelector(
          `#ProductSelector-${this.mainProductSectionId} product-selector`
        );

        if (selector) return selector;
      }

      return this.mainProductRoot?.querySelector('product-selector') || null;
    }

    getMainVariant() {
      const selectedId = Number(this.mainVariantInput?.value || this.mainVariants[0]?.id);

      return this.mainVariants.find(variant => Number(variant.id) === selectedId) || this.mainVariants[0];
    }

    findVariantBySelections(variants, selections, onlyAvailable = false) {
      return variants.find(variant => {
        if (onlyAvailable && !variant.available) return false;

        return selections.every((value, index) => {
          if (!value) return true;

          return variant[`option${index + 1}`] === value;
        });
      });
    }

    getRelatedVariant() {
      if (!this.relatedVariants.length) return null;
      if (!this.relatedOptionSelects.length) return this.relatedVariants[0];

      const selections = this.relatedOptionSelects.map(select => select.value);

      return (
        this.findVariantBySelections(this.relatedVariants, selections, true) ||
        this.findVariantBySelections(this.relatedVariants, selections) ||
        this.relatedVariants[0]
      );
    }

    updateDropdownInput(select) {
      select
        ?.closest('dropdown-input')
        ?.update();
    }

    syncMainOptionsFromPrimary() {
      const mainVariant = this.getMainVariant();
      if (!mainVariant || !this.mainOptionSelects.length) return;

      this.mainOptionSelects.forEach((select, index) => {
        const value = mainVariant[`option${index + 1}`];
        if (!value) return;

        select.value = value;
        this.updateDropdownInput(select);
      });
    }

    syncPrimaryProductSelector(optionName, value) {
      if (!this.mainProductSelector || !optionName) return;

      const optionControls = Array.from(
        this.mainProductSelector.querySelectorAll('[name]')
      ).filter(control => control.name === `options[${optionName}]`);

      const radio = optionControls.find(
        control => control.matches('input[type="radio"]') && control.value === value
      );

      if (radio) {
        if (!radio.checked) {
          radio.checked = true;
        }

        radio.dispatchEvent(new Event('change', { bubbles: true }));
        return;
      }

      const select = optionControls.find(control => control.tagName === 'SELECT');

      if (select) {
        select.value = value;
        select.dispatchEvent(new Event('change', { bubbles: true }));
      }
    }

    syncRelatedOptionAvailability() {
      if (!this.relatedOptionSelects.length) return;

      const selections = this.relatedOptionSelects.map(select => select.value);

      this.relatedOptionSelects.forEach((select, index) => {
        let matchingVariants = this.relatedVariants;

        for (let previousIndex = 0; previousIndex < index; previousIndex += 1) {
          matchingVariants = matchingVariants.filter(
            variant => variant[`option${previousIndex + 1}`] === selections[previousIndex]
          );
        }

        const optionKey = `option${index + 1}`;
        const availableValues = [...new Set(
          matchingVariants
            .filter(variant => variant.available)
            .map(variant => variant[optionKey])
            .filter(Boolean)
        )];

        Array.from(select.options).forEach(option => {
          option.disabled = availableValues.length
            ? !availableValues.includes(option.value)
            : false;
        });

        if (availableValues.length && !availableValues.includes(select.value)) {
          const firstAvailable = Array.from(select.options).find(option => !option.disabled);

          if (firstAvailable) {
            select.value = firstAvailable.value;
            selections[index] = firstAvailable.value;
          }
        } else {
          selections[index] = select.value;
        }

        this.updateDropdownInput(select);
      });
    }

    onRelatedVariantChange() {
      this.clearError();
      this.syncRelatedOptionAvailability();
      this.render();
    }

    onMainOptionChange(event) {
      this.clearError();
      this.syncPrimaryProductSelector(
        event.target.dataset.optionName,
        event.target.value
      );
    }

    onMainVariantChange() {
      this.clearError();
      this.render();
    }

    render() {
      const mainVariant = this.getMainVariant();
      this.syncMainOptionsFromPrimary();
      this.syncRelatedOptionAvailability();
      const relatedVariant = this.getRelatedVariant();

      if (!mainVariant || !relatedVariant) return;

      if (this.mainImage && mainVariant.image) {
        this.mainImage.src = mainVariant.image;
      }

      if (this.mainPrice) {
        this.mainPrice.textContent = this.formatMoney(mainVariant.price);
      }

      if (this.relatedPrice) {
        this.relatedPrice.textContent = this.formatMoney(relatedVariant.price);
      }

      if (this.relatedCompareAt) {
        const hasCompareAtPrice = Number(relatedVariant.compareAtPrice) > Number(relatedVariant.price);
        this.relatedCompareAt.textContent = hasCompareAtPrice
          ? this.formatMoney(relatedVariant.compareAtPrice)
          : '';
        this.relatedCompareAt.classList.toggle('hidden', !hasCompareAtPrice);
      }

      if (this.relatedImage && relatedVariant.image) {
        this.relatedImage.src = relatedVariant.image;
      }

      if (this.totalPrice) {
        this.totalPrice.textContent = this.formatMoney(
          Number(mainVariant.price) + Number(relatedVariant.price)
        );
      }

      if (this.relatedStatus) {
        this.relatedStatus.classList.toggle('hidden', relatedVariant.available);
      }

      this.updateButtonState(mainVariant, relatedVariant);
    }

    updateButtonState(mainVariant, relatedVariant) {
      if (!this.submitButton) return;

      const canAddBoth = Boolean(mainVariant?.available && relatedVariant?.available) && !this.isLoading;
      this.submitButton.disabled = !canAddBoth;
      this.submitButton.textContent = this.isLoading
        ? this.config.buttonLoadingText
        : this.config.buttonText;
    }

    onSubmit() {
      const mainVariant = this.getMainVariant();
      const relatedVariant = this.getRelatedVariant();

      if (!mainVariant?.available) {
        this.showError('Selecione uma variacao disponivel do produto principal.');
        return;
      }

      if (!relatedVariant?.available) {
        this.showError('Selecione uma variacao disponivel no produto do compre junto.');
        return;
      }

      this.clearError();
      this.isLoading = true;
      this.updateButtonState(mainVariant, relatedVariant);

      const body = {
        items: [
          { id: Number(mainVariant.id), quantity: 1 },
          { id: Number(relatedVariant.id), quantity: 1 }
        ],
        sections: this.cartDrawer
          ? this.cartDrawer.getSectionsToRender().map(section => section.section)
          : [],
        sections_url: window.location.pathname
      };

      fetch(`${routes.cart_add_url}`, {
        ...fetchConfig(),
        body: JSON.stringify(body)
      })
        .then(response => response.json())
        .then(response => {
          if (response.status) {
            this.showError(response.description || 'Nao foi possivel adicionar os produtos.');
            return;
          }

          if (this.cartDrawer && response.sections) {
            this.cartDrawer.renderContents(response);
            return;
          }

          window.location.href = routes.cart_url;
        })
        .catch(() => {
          this.showError('Nao foi possivel adicionar os produtos.');
        })
        .finally(() => {
          this.isLoading = false;
          this.updateButtonState(this.getMainVariant(), this.getRelatedVariant());
        });
    }

    showError(message) {
      if (!this.errorWrapper) return;

      this.errorWrapper.textContent = message;
      this.errorWrapper.classList.remove('hidden');
    }

    clearError() {
      if (!this.errorWrapper) return;

      this.errorWrapper.textContent = '';
      this.errorWrapper.classList.add('hidden');
    }

    formatMoney(cents, format = this.config.moneyFormat) {
      const placeholderRegex = /\{\{\s*(\w+)\s*\}\}/;
      const formatString = format || '${{amount}}';

      const formatWithDelimiters = (
        amount,
        precision = 2,
        thousands = '.',
        decimal = ','
      ) => {
        if (Number.isNaN(amount) || amount === null) {
          return '0';
        }

        const number = (amount / 100).toFixed(precision);
        const parts = number.split('.');
        const dollars = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, thousands);
        const centsPart = parts[1] ? decimal + parts[1] : '';

        return dollars + centsPart;
      };

      return formatString.replace(placeholderRegex, (match, placeholder) => {
        switch (placeholder) {
          case 'amount':
            return formatWithDelimiters(cents, 2, '.', ',');
          case 'amount_no_decimals':
            return formatWithDelimiters(cents, 0, '.', ',');
          case 'amount_with_comma_separator':
            return formatWithDelimiters(cents, 2, ',', '.');
          case 'amount_with_space_separator':
            return formatWithDelimiters(cents, 2, ' ', ',');
          case 'amount_no_decimals_with_comma_separator':
            return formatWithDelimiters(cents, 0, ',', '.');
          case 'amount_no_decimals_with_space_separator':
            return formatWithDelimiters(cents, 0, ' ', ',');
          case 'amount_with_apostrophe_separator':
            return formatWithDelimiters(cents, 2, "'", '.');
          default:
            return formatWithDelimiters(cents, 2, '.', ',');
        }
      });
    }
  }

  customElements.define('compre-junto', CompreJunto);
})();
